import type {
  AvailabilitySlotDto,
  PublicBookingDto,
  PublicClinicDto,
  PublicPractitionerDto,
  ServiceDto,
} from "@clinic/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, api } from "../api";
import { fieldErrors, hasFieldErrors } from "../forms";
import { formatDateTime, formatMoney, LanguageSwitcher, storedLocale, useT } from "../i18n";
import { usePageMeta } from "../seo";
import { SlotPicker } from "../slot-picker";
import { Alert, Button, Card, Field, FullPageSpinner, Textarea } from "../ui";

function usePublicClinic(slug: string) {
  const { setLocale } = useT();
  const clinic = useQuery({
    queryKey: ["public-clinic", slug],
    queryFn: () => api<PublicClinicDto>("GET", `/public/clinics/${slug}`),
    staleTime: 5 * 60_000,
  });
  // The clinic's language applies until the visitor picks one themselves (or the URL names one).
  useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search).has("lang");
    if (clinic.data && !storedLocale() && !urlLang) setLocale(clinic.data.defaultLocale, false);
  }, [clinic.data, setLocale]);
  return clinic;
}

function BookingShell({ clinic, children }: { clinic?: PublicClinicDto; children: ReactNode }) {
  const { t } = useT();
  usePageMeta("book", clinic);
  return (
    <div className="min-h-screen">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-4 py-5">
        <div>
          <h1 className="text-lg font-semibold">{clinic?.name ?? t("common.app_name")}</h1>
          {clinic?.address || clinic?.phone ? (
            <p className="text-sm text-slate-500">{[clinic.address, clinic.phone].filter(Boolean).join(" · ")}</p>
          ) : null}
        </div>
        <LanguageSwitcher />
      </header>
      <main className="mx-auto max-w-3xl space-y-4 px-4 pb-16">{children}</main>
      <footer className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 pb-8 text-xs text-slate-500">
        <Link to="/" className="inline-flex items-center gap-1.5 hover:underline">
          <img src="/logo-mark.png" alt="" width={20} height={20} className="h-5 w-5" />
          {t("book.powered_by")}
        </Link>
        <span aria-hidden="true">·</span>
        <Link to="/privacy" className="hover:underline">
          {t("footer.privacy")}
        </Link>
      </footer>
    </div>
  );
}

function Step({
  number,
  title,
  summary,
  onChange,
  children,
}: {
  number: number;
  title: string;
  summary?: ReactNode;
  onChange?: () => void;
  children?: ReactNode;
}) {
  const { t } = useT();
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-3 font-semibold">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-brand-700 text-sm text-white">{number}</span>
          {title}
        </h2>
        {summary !== undefined && onChange ? (
          <button type="button" onClick={onChange} className="text-sm font-medium text-brand-700 hover:underline">
            {t("book.change")}
          </button>
        ) : null}
      </div>
      {summary !== undefined ? (
        <p className="mt-2 pl-10 text-sm text-slate-700">{summary}</p>
      ) : (
        <div className="mt-4">{children}</div>
      )}
    </Card>
  );
}

const ANY = "any";

export function BookPage() {
  const { slug = "" } = useParams();
  const { t, tError, locale } = useT();
  const clinic = usePublicClinic(slug);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [practitionerId, setPractitionerId] = useState<string | null>(null);
  const [slot, setSlot] = useState<AvailabilitySlotDto | null>(null);
  const [done, setDone] = useState<PublicBookingDto | null>(null);
  const [manageToken, setManageToken] = useState<string | null>(null);

  const services = useQuery({
    queryKey: ["public-services", slug],
    enabled: clinic.isSuccess,
    queryFn: () => api<{ items: ServiceDto[] }>("GET", `/public/clinics/${slug}/services`),
  });
  const practitioners = useQuery({
    queryKey: ["public-practitioners", slug, serviceId],
    enabled: Boolean(serviceId),
    queryFn: () =>
      api<{ items: PublicPractitionerDto[] }>("GET", `/public/clinics/${slug}/practitioners?serviceId=${serviceId}`),
  });
  const book = useMutation({
    mutationFn: (input: unknown) =>
      api<PublicBookingDto & { manageToken?: string }>("POST", `/public/clinics/${slug}/bookings`, input),
    onSuccess: (b) => {
      setManageToken(b.manageToken ?? null);
      setDone(b);
    },
  });

  if (clinic.isPending) return <FullPageSpinner />;
  if (clinic.isError) {
    return (
      <BookingShell>
        <Alert>
          {clinic.error instanceof ApiError && clinic.error.status === 404
            ? t("book.clinic_not_found")
            : tError(clinic.error)}
        </Alert>
      </BookingShell>
    );
  }
  const c = clinic.data;
  const service = services.data?.items.find((s) => s.id === serviceId) ?? null;
  const practitioner = practitioners.data?.items.find((p) => p.id === practitionerId) ?? null;
  const fields = fieldErrors(book.error);

  if (!c.acceptingBookings && !done) {
    return (
      <BookingShell clinic={c}>
        <Card>
          <h2 className="font-semibold">{t("book.closed_title")}</h2>
          <p className="mt-2 text-sm text-slate-600">{t("book.closed_text")}</p>
        </Card>
      </BookingShell>
    );
  }

  if (done) {
    return (
      <BookingShell clinic={c}>
        <Card>
          <h2 className="text-xl font-semibold">{t("book.confirmed_title")}</h2>
          <p className="mt-2 text-slate-700">{t("book.confirmed_text", { email: done.customer.email })}</p>
          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-[auto_1fr]">
            <dt className="text-slate-500">{t("book.service")}</dt>
            <dd>{done.service.name}</dd>
            <dt className="text-slate-500">{t("book.practitioner")}</dt>
            <dd>{done.practitioner.name}</dd>
            <dt className="text-slate-500">{t("book.when")}</dt>
            <dd className="">{formatDateTime(locale, done.startsAt, c.timezone)}</dd>
          </dl>
          <div className="mt-6 flex flex-wrap gap-3">
            {manageToken ? (
              <Link
                to={`/book/${slug}/manage/${manageToken}`}
                className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white"
              >
                {t("book.view_booking")}
              </Link>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setDone(null);
                setSlot(null);
                setManageToken(null);
              }}
            >
              {t("book.book_another")}
            </Button>
          </div>
        </Card>
      </BookingShell>
    );
  }

  return (
    <BookingShell clinic={c}>
      <Step
        number={1}
        title={t("book.step_service")}
        summary={
          service
            ? `${service.name} · ${t("common.minutes", { n: service.durationMinutes })} · ${service.priceCents === 0 ? t("common.free") : formatMoney(locale, service.priceCents, service.currency)}`
            : undefined
        }
        onChange={() => {
          setServiceId(null);
          setPractitionerId(null);
          setSlot(null);
        }}
      >
        {services.isPending ? (
          <FullPageSpinner />
        ) : services.isError ? (
          <Alert>{tError(services.error)}</Alert>
        ) : services.data.items.length === 0 ? (
          <p className="text-sm text-slate-600">{t("book.no_services")}</p>
        ) : (
          <ul className="grid gap-2">
            {services.data.items.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setServiceId(s.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-lg px-4 py-3 text-left ring-1 ring-slate-200 hover:bg-brand-50"
                >
                  <span>
                    <span className="block font-medium">{s.name}</span>
                    {s.description ? <span className="block text-sm text-slate-500">{s.description}</span> : null}
                  </span>
                  <span className="shrink-0 text-sm text-slate-600">
                    {t("common.minutes", { n: s.durationMinutes })} ·{" "}
                    {s.priceCents === 0 ? t("common.free") : formatMoney(locale, s.priceCents, s.currency)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Step>

      {serviceId ? (
        <Step
          number={2}
          title={t("book.step_practitioner")}
          summary={
            practitionerId ? (practitionerId === ANY ? t("book.any_practitioner") : practitioner?.name) : undefined
          }
          onChange={() => {
            setPractitionerId(null);
            setSlot(null);
          }}
        >
          {practitioners.isPending ? (
            <FullPageSpinner />
          ) : practitioners.isError ? (
            <Alert>{tError(practitioners.error)}</Alert>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {practitioners.data.items.length > 1 ? (
                <li>
                  <button
                    type="button"
                    onClick={() => setPractitionerId(ANY)}
                    className="w-full rounded-lg px-4 py-3 text-left font-medium ring-1 ring-slate-200 hover:bg-brand-50"
                  >
                    {t("book.any_practitioner")}
                  </button>
                </li>
              ) : null}
              {practitioners.data.items.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setPractitionerId(p.id)}
                    className="w-full rounded-lg px-4 py-3 text-left ring-1 ring-slate-200 hover:bg-brand-50"
                  >
                    <span className="block font-medium">{p.name}</span>
                    {p.title ? <span className="block text-sm text-slate-500">{p.title}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Step>
      ) : null}

      {serviceId && practitionerId ? (
        <Step
          number={3}
          title={t("book.step_time")}
          summary={slot ? <span className="">{formatDateTime(locale, slot.startsAt, c.timezone)}</span> : undefined}
          onChange={() => setSlot(null)}
        >
          <SlotPicker
            slug={slug}
            serviceId={serviceId}
            practitionerId={practitionerId === ANY ? undefined : practitionerId}
            timezone={c.timezone}
            selected={slot}
            onSelect={setSlot}
          />
        </Step>
      ) : null}

      {serviceId && practitionerId && slot ? (
        <Step number={4} title={t("book.step_details")}>
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              const v = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
              book.mutate({
                serviceId,
                practitionerId: practitionerId === ANY ? undefined : practitionerId,
                startsAt: slot.startsAt,
                customer: { fullName: v.fullName, email: v.email, phone: v.phone ?? "" },
                notes: v.notes ?? "",
                locale,
              });
            }}
          >
            {book.error && !hasFieldErrors(book.error) ? (
              <div className="sm:col-span-2">
                <Alert>{tError(book.error)}</Alert>
              </div>
            ) : null}
            <Field
              label={t("book.full_name")}
              name="fullName"
              autoComplete="name"
              required
              error={fields["customer.fullName"]}
            />
            <Field
              label={t("book.email")}
              name="email"
              type="email"
              autoComplete="email"
              required
              error={fields["customer.email"]}
            />
            <Field
              label={t("book.phone")}
              name="phone"
              type="tel"
              autoComplete="tel"
              error={fields["customer.phone"]}
            />
            <div className="sm:col-span-2">
              <Textarea label={t("book.notes")} name="notes" hint={t("book.notes_hint")} error={fields.notes} />
            </div>
            <p className="text-sm text-slate-600 sm:col-span-2">
              {t("book.privacy_notice", { clinic: c.name })}{" "}
              <Link to="/privacy" target="_blank" className="font-medium text-brand-700 hover:underline">
                {t("book.privacy_link")}
              </Link>
            </p>
            <div className="sm:col-span-2">
              <Button busy={book.isPending}>{t("book.confirm_button")}</Button>
            </div>
          </form>
        </Step>
      ) : null}
    </BookingShell>
  );
}

export function ManageBookingPage() {
  const { slug = "", token = "" } = useParams();
  const { t, tError, locale } = useT();
  const qc = useQueryClient();
  const clinic = usePublicClinic(slug);
  const key = ["public-booking", slug, token];
  const booking = useQuery({
    queryKey: key,
    enabled: clinic.isSuccess,
    queryFn: () => api<PublicBookingDto>("GET", `/public/clinics/${slug}/bookings/${token}`),
  });
  const [mode, setMode] = useState<"idle" | "move" | "cancel">("idle");
  const [slot, setSlot] = useState<AvailabilitySlotDto | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const cancel = useMutation({
    mutationFn: (reason: string) =>
      api<PublicBookingDto>("POST", `/public/clinics/${slug}/bookings/${token}/cancel`, { reason }),
    onSuccess: (b) => {
      qc.setQueryData(key, b);
      setMode("idle");
      setNotice(t("book.cancelled_done"));
    },
  });
  const move = useMutation({
    mutationFn: (startsAt: string) =>
      api<PublicBookingDto>("POST", `/public/clinics/${slug}/bookings/${token}/reschedule`, { startsAt }),
    onSuccess: (b) => {
      qc.setQueryData(key, b);
      qc.invalidateQueries({ queryKey: ["availability"] });
      setMode("idle");
      setSlot(null);
      setNotice(t("book.moved_done"));
    },
  });

  if (clinic.isPending || (clinic.isSuccess && booking.isPending)) return <FullPageSpinner />;
  if (clinic.isError) {
    return (
      <BookingShell>
        <Alert>{t("book.clinic_not_found")}</Alert>
      </BookingShell>
    );
  }
  const c = clinic.data;
  if (booking.isError) {
    return (
      <BookingShell clinic={c}>
        <Alert>
          {booking.error instanceof ApiError && booking.error.status === 404
            ? t("book.manage_not_found")
            : tError(booking.error)}
        </Alert>
      </BookingShell>
    );
  }
  const b = booking.data!;
  const canChange = b.status === "confirmed" && Date.now() < Date.parse(b.cancellableUntil);
  const error = cancel.error ?? move.error;

  return (
    <BookingShell clinic={c}>
      <Card>
        <h2 className="text-xl font-semibold">{t("book.manage_title")}</h2>
        {notice ? (
          <div className="mt-3">
            <Alert tone="success">{notice}</Alert>
          </div>
        ) : null}
        {b.status !== "confirmed" && !notice ? (
          <div className="mt-3">
            <Alert tone="info">{t(`book.status_${b.status}`)}</Alert>
          </div>
        ) : null}
        <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-[auto_1fr]">
          <dt className="text-slate-500">{t("book.name")}</dt>
          <dd>{b.customer.fullName}</dd>
          <dt className="text-slate-500">{t("book.service")}</dt>
          <dd>
            {b.service.name} · {t("common.minutes", { n: b.service.durationMinutes })} ·{" "}
            {b.service.priceCents === 0
              ? t("common.free")
              : formatMoney(locale, b.service.priceCents, b.service.currency)}
          </dd>
          <dt className="text-slate-500">{t("book.practitioner")}</dt>
          <dd>{b.practitioner.name}</dd>
          <dt className="text-slate-500">{t("book.when")}</dt>
          <dd className={`${b.status === "cancelled" ? "line-through" : ""}`}>
            {formatDateTime(locale, b.startsAt, c.timezone)}
          </dd>
        </dl>
        {b.status === "confirmed" ? (
          <p className="mt-4 text-sm text-slate-600">
            {canChange
              ? t("book.cancellable_until", { when: formatDateTime(locale, b.cancellableUntil, c.timezone) })
              : t("book.too_late")}
          </p>
        ) : null}
        {error ? (
          <div className="mt-3">
            <Alert>{tError(error)}</Alert>
          </div>
        ) : null}
        {canChange ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              type="button"
              variant={mode === "move" ? "secondary" : "primary"}
              onClick={() => setMode(mode === "move" ? "idle" : "move")}
            >
              {mode === "move" ? t("book.keep_time") : t("book.move_button")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setMode(mode === "cancel" ? "idle" : "cancel")}>
              {t("book.cancel_button")}
            </Button>
          </div>
        ) : null}
      </Card>

      {mode === "move" && canChange ? (
        <Card>
          <h2 className="font-semibold">{t("book.move_title")}</h2>
          <div className="mt-4">
            <SlotPicker slug={slug} serviceId={b.serviceId} timezone={c.timezone} selected={slot} onSelect={setSlot} />
          </div>
          <div className="mt-4">
            <Button
              type="button"
              disabled={!slot}
              busy={move.isPending}
              onClick={() => slot && move.mutate(slot.startsAt)}
            >
              {t("book.move_button")}
            </Button>
          </div>
        </Card>
      ) : null}

      {mode === "cancel" && canChange ? (
        <Card>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              cancel.mutate(String(new FormData(e.currentTarget).get("reason") ?? ""));
            }}
          >
            <p className="font-medium">{t("book.cancel_confirm")}</p>
            <Field label={t("book.cancel_reason")} name="reason" />
            <div className="flex gap-2">
              <Button busy={cancel.isPending}>{t("book.cancel_button")}</Button>
              <Button type="button" variant="ghost" onClick={() => setMode("idle")}>
                {t("common.back")}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <p className="text-center text-sm">
        <Link to={`/book/${slug}`} className="font-medium text-brand-700 hover:underline">
          {t("book.book_another")}
        </Link>
      </p>
    </BookingShell>
  );
}
